// apps/web/src/features/russia-requests/components/RussiaRequestDrawerHeaderActions.tsx
//
// Desktop/narrow header action cluster for the Russia request drawer.
// #2201 converges it with China's all-in-«⋮» model (#2200): every business
// action — the transitions, the edit-draft affordance, and duplicate —
// lives inside a single «⋮» overflow menu; only the Close icon renders
// outside it.
//
// WHY: extracted from RussiaRequestDrawerHeader.tsx so the overflow logic
//   does not push the header over the 400-line soft cap, mirroring how the
//   China cluster is split out of `RequestDrawerHeader`.

import { useTranslation } from 'react-i18next';

import { IconButton, Menu, MenuItem } from '../../../ui/index.js';
import type { RussiaTransitionAction } from '../role.js';

// WHY (#2120): the Duplicate action (label `russiaRequests:actions.duplicate`).
//   Owner-only visibility is decided by the caller; the menu item only needs
//   the in-flight flag (single-flight disable) + click handler.
export interface RussiaDuplicateButtonProps {
  visible: boolean;
  pending: boolean;
  onClick: () => void;
}

// WHY (#2201, mirror of #2200 D-4): float the (single) primary transition to
//   the front with a STABLE sort so it heads the «⋮» menu; the remaining
//   transitions follow in their matrix order.
const rank = (a: RussiaTransitionAction): number => (a.kind === 'primary' ? 0 : 1);

/**
 * @aiPurpose desktop/narrow header action cluster for the Russia request
 *   drawer — a single «⋮» menu (#2201) holding every business action, plus
 *   the standalone Close icon button.
 * @aiInvariant all Russia business actions live inside `ui/Menu` as
 *   `MenuItem`s (`role="menuitem"`): on desktop (`!narrow`) the primary-first
 *   transition set, then edit-draft, then duplicate; on narrow only
 *   edit-draft + duplicate (transitions live in the
 *   `RussiaRequestDrawerStickyActionBar`). The «⋮» renders only when at least
 *   one business action is visible — never an empty menu. Only Close renders
 *   outside the menu. Russia transitions carry no per-action disabled state —
 *   the global `pending` flag gates every transition menu item.
 */
export function RussiaRequestDrawerHeaderActions(props: {
  actions: RussiaTransitionAction[];
  narrow: boolean;
  pending: boolean;
  onAction: (a: RussiaTransitionAction) => void;
  onClose: () => void;
  canEdit: boolean;
  onEdit: () => void;
  duplicate: RussiaDuplicateButtonProps;
}) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const { actions, narrow, pending, onAction, onClose, canEdit, onEdit, duplicate } = props;

  // Mirror of China D-5: transitions collapse into the «⋮» on desktop
  //   (primary-first); on narrow they are excluded — the sticky action bar
  //   owns them there.
  const orderedTransitions = narrow ? [] : [...actions].sort((a, b) => rank(a) - rank(b));
  const hasBusinessActions = orderedTransitions.length > 0 || canEdit || duplicate.visible;

  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        flex: 'none',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
    >
      {hasBusinessActions && (
        <Menu
          trigger={(triggerProps) => (
            <IconButton
              {...triggerProps}
              icon="more-vertical"
              label={t('common:actions.more')}
              data-testid="russia-request-drawer-actions-more"
            />
          )}
        >
          {orderedTransitions.map((a) => (
            // Mirror of China D-10: transitions are `MenuItem`s (role="menuitem"),
            //   never raw Buttons — Menu close/keyboard nav depends on the role.
            //   Russia carries no per-action disabled/reason, so the global
            //   `pending` flag gates them.
            <MenuItem key={a.key} disabled={pending} onClick={() => onAction(a)}>
              {t(a.labelKey)}
            </MenuItem>
          ))}
          {canEdit && <MenuItem onClick={onEdit}>{t('russiaRequests:drawer.editDraft')}</MenuItem>}
          {duplicate.visible && (
            <MenuItem disabled={duplicate.pending} onClick={duplicate.onClick}>
              {t('russiaRequests:actions.duplicate')}
            </MenuItem>
          )}
        </Menu>
      )}
      <IconButton
        icon="x"
        label={t('common:actions.close')}
        onClick={onClose}
        data-testid="russia-request-drawer-close"
      />
    </div>
  );
}
